import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Input,
  SimpleGrid,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useBillItems } from "../context/tableItemsContext";
import io from "socket.io-client";
import { useTableTime } from "../context/tableTimeContext";
import { useKotContext } from "../context/kotContext";
import SubTableUpdateModal from "./subTableUpdateModal";
import { MdOutlineSubject } from "react-icons/md";

const socket = io("https://api.rasoi.launchlysoftware.in");

const TableStatus = () => {
  const { elapsedTime } = useTableTime();
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { holdTimes, fetchKotNo } = useKotContext();
  const [subTableNo, setSubTableNo] = useState(null);

  const customerId = localStorage.getItem("customerId");
  const recentQuickBills =
    JSON.parse(localStorage.getItem("recentQuickBills")) || [];

  const [order, setOrder] = useState({});
  const [fields, setFields] = useState({});
  const { billItems, fetchTableBillItems } = useBillItems();
  const [data, setData] = useState([]);
  const [subTable, setSubTable] = useState(null);
  // ⬆️ inside component state
  const [refreshedTables, setRefreshedTables] = useState([]);

  const [filter, setFilter] = useState({
    ac: "",
    status: "",
  });

  const [searchTable, setSearchTable] = useState("");

  const navigate = useNavigate();
  const kotSaved = JSON.parse(localStorage.getItem("kotPrints")) || [];

  const handleOpenModal = (tableNo, sub) => {
    setSubTableNo(tableNo);
    setSubTable(sub);
    onOpen();
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `https://api.rasoi.launchlysoftware.in/api/getTableData/${customerId}`,
      );
      setData(response.data.table);

      response.data.table.forEach((table) => {
        fetchTableData(table.tableNo);
        fetchKotNo(customerId, table.tableNo);
      });
    } catch (error) {
      console.error("Error fetching table data:", error);
    }
  };

  const fetchTableData = async (tableNo) => {
    try {
      const response = await axios.get(
        `https://api.rasoi.launchlysoftware.in/api/getItems/${customerId}/${tableNo}`,
      );
      setOrder((prev) => ({
        ...prev,
        [tableNo]: response.data.items,
      }));
      setFields((prev) => ({
        ...prev,
        [tableNo]: response.data,
      }));
    } catch (error) {
      console.error("Error fetching table items:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [customerId]);

  useEffect(() => {
    const customerId = localStorage.getItem("customerId");
    socket.emit("joinRoom", customerId);

    socket.on("kotGenerated", (data) => {
      fetchTableData(data.tableNo);
      fetchTableBillItems(customerId, data.tableNo);
    });

    return () => {
      socket.off("kotGenerated");
    };
  }, []);

  const getElapsedMinutes = (createdAt) => {
    const createdAtTime = new Date(createdAt);
    const now = new Date();
    const differenceInMinutes = Math.floor((now - createdAtTime) / (1000 * 60));
    return `${differenceInMinutes} mins`;
  };

  let filteredData = data.filter((item) => {
    return (
      (filter.ac === "" || item.ac === filter.ac) &&
      (filter.status === "" || item.status === filter.status)
    );
  });

  if (searchTable.trim() !== "") {
    filteredData = filteredData.filter((item) =>
      item.tableNo.toLowerCase().includes(searchTable.toLowerCase()),
    );
  }

  return (
    <Box p={10}>
      {/* 🔍 Search + Filters */}
      <Box
        mb={4}
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap={2}
      >
        <Input
          placeholder="Search Table..."
          value={searchTable}
          onChange={(e) => setSearchTable(e.target.value)}
          width="200px"
          size="sm"
        />

        <Button
          fontSize="sm"
          colorScheme={filter.status === "" ? "blue" : "gray"}
          onClick={() => setFilter({ ...filter, status: "" })}
        >
          All
        </Button>
        <Button
          fontSize="sm"
          colorScheme={filter.ac === "Ac" ? "blue" : "gray"}
          onClick={() => setFilter({ ...filter, ac: "Ac" })}
        >
          Ac
        </Button>
        <Button
          fontSize="sm"
          colorScheme={filter.ac === "Non-Ac" ? "blue" : "gray"}
          onClick={() => setFilter({ ...filter, ac: "Non-Ac" })}
        >
          Non-Ac
        </Button>
      </Box>

      <SimpleGrid
        columns={{ base: 4, md: 6, lg: 8, xl: 12 }}
        spacing={4}
        mb={3}
        p={4}
      >
        {filteredData.map((table) => {
          const tableServer = kotSaved.find(
            (item) => item.tableNo === table.tableNo,
          )?.serverName;
          const tableOrder = order[table.tableNo] || [];
          const tableFields = fields[table.tableNo] || {};
          const total = tableFields.total || 0;
          const pax = tableFields.pax;
          const createdAt = holdTimes[table.tableNo];

          // ✅ check from context
          const hasBill = billItems.some(
            (bill) => bill.tableNo === table.tableNo && bill.billNo,
          );

          // ✅ check from localStorage
          const storedBills =
            JSON.parse(localStorage.getItem("billItems")) || [];
          const hasDiscount = storedBills.some(
            (bill) =>
              bill.tableNo === table.tableNo &&
              bill.discountPercentage !== null &&
              bill.discountPercentage !== "" &&
              bill.discountPercentage !== undefined,
          );

          // ✅ combined condition
          const showPrint = hasBill || hasDiscount;

          const elapsedTimeLabel = createdAt
            ? getElapsedMinutes(createdAt)
            : "";

          const tooltipLabel =
            tableOrder.length > 0
              ? `${tableOrder
                  .map((item) => `${item.name} x${item.quantity}`)
                  .join(", ")}` +
                ` | Total: ₹${total}` +
                (createdAt ? ` | Time: ${elapsedTimeLabel}` : "") +
                (pax !== undefined ? ` | Pax: ${pax}` : "") +
                (tableServer ? ` | Server: ${tableServer}` : "")
              : `No items ordered${
                  tableServer ? ` | Server: ${tableServer}` : ""
                }`;

          return (
            <Tooltip key={table.tableNo} label={tooltipLabel}>
              <Box position="relative" w="full">
                {/* Table Box */}
                <Box
                  border="2px dashed"
                  borderColor={!table.status ? "green.400" : "red.400"}
                  bg={
                    refreshedTables.includes(table.tableNo)
                      ? "green.400"
                      : hasBill
                      ? "yellow.300"
                      : tooltipLabel === "No items ordered"
                      ? "green.400"
                      : "yellow.300"
                  }
                  borderRadius="md"
                  w="full"
                  h="80px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  cursor="pointer"
                  color={
                    refreshedTables.includes(table.tableNo)
                      ? "white"
                      : tooltipLabel === "No items ordered"
                      ? "white"
                      : "gray.800"
                  }
                  position="relative"
                  transition="all 0.2s"
                  _hover={{ transform: "scale(1.05)", boxShadow: "lg" }}
                  onClick={() =>
                    navigate("/restaurant", {
                      state: { tableNo: table.tableNo, ac: table.ac },
                    })
                  }
                >
                  {showPrint && (
                    <Button
                      size="xs"
                      p={1}
                      position="absolute"
                      top="0"
                      left="0"
                      fontSize="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log(
                          "🖨 Printing Bill/Discount for",
                          table.tableNo,
                        );
                      }}
                    >
                      🖨
                    </Button>
                  )}

                  {/* 📋 SubTable Button */}
                  {!table.tableNo.includes("-") && (
                    <Button
                      size="xs"
                      p={1}
                      position="absolute"
                      top="0"
                      right="0"
                      fontSize="sm"
                      colorScheme="blue"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(table.tableNo, table.subTable);
                      }}
                    >
                      <MdOutlineSubject />
                    </Button>
                  )}

                  <Text>{table.tableNo}</Text>
                </Box>

                {/* <Button
  size="xs"
  fontSize="xs"
  colorScheme="green"
  position="absolute"
  bottom="-16px"
  right="-16px"
  borderRadius="full"
  minW="20px"
  h="20px"
  p={0}
  onClick={(e) => {
    e.stopPropagation();
    console.log("🔄 Refreshing & Clearing table", table.tableNo);

    // 1. Just clear local states for this table
    setOrder((prev) => ({ ...prev, [table.tableNo]: [] }));
    setFields((prev) => ({ ...prev, [table.tableNo]: {} }));

    // 2. Mark this table green
    setRefreshedTables((prev) => [...new Set([...prev, table.tableNo])]);

    // 3. Refresh bill status from server
    fetchTableData(table.tableNo);
    fetchTableBillItems(customerId, table.tableNo);
  }}
>
  ↻
</Button> */}
              </Box>
            </Tooltip>
          );
        })}
      </SimpleGrid>

      <SubTableUpdateModal
        customerId={customerId}
        isOpen={isOpen}
        onClose={onClose}
        onOpen={onOpen}
        subTableNo={subTableNo}
        setSubTableNo={setSubTableNo}
        subTable={subTable}
        setSubTable={setSubTable}
      />
    </Box>
  );
};

export default TableStatus;
